import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiUsers,
  FiCheckCircle,
  FiTrash2,
  FiSearch,
  FiUser,
} from "react-icons/fi";
import api from "../../utils/api";
import { useLanguages } from "../../hooks/useLanguages";
import Pagination from "../../components/common/Pagination";
import TableSearch from "../../components/common/TableSearch";
import { usePagination } from "../../hooks/usePagination";



const StaffManagement = () => {
   const { supportedLanguages } = useLanguages()
  const navigate = useNavigate();
  const isStaffRoute = window.location.pathname.includes("/restaurant-staff");
  const routePrefix = isStaffRoute ? "/restaurant-staff/employee" : "/restaurant";
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState("en");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStaffData();
  }, []);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      setError("");

      const isStaff = window.location.pathname.includes("/restaurant-staff");
      const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";

      const staffResponse = await api.get(`${apiPrefix}/staff/all`);
      // response.data could be the array directly, or nested in .data
      let staffData = [];
      if (Array.isArray(staffResponse.data)) {
        staffData = staffResponse.data;
      } else if (staffResponse.data?.data && Array.isArray(staffResponse.data.data)) {
        staffData = staffResponse.data.data;
      } else if (staffResponse.data && typeof staffResponse.data === 'object') {
        // Could be a single object or something else
        staffData = Array.isArray(Object.values(staffResponse.data)) ? [] : [];
      }
      setStaff(staffData);

      const rolesResponse = await api.get(`${apiPrefix}/staff/roles`);
      const rolesData = rolesResponse.data.data || rolesResponse.data;
      setRoles(rolesData?.roles || []);
      setPermissions(rolesData?.permissions || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load staff data");
    } finally {
      setLoading(false);
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role ? role.name : "No Role Assigned";
  };

  const getRole = (roleId) => {
    return roles.find((r) => r.id === roleId);
  };

  const parsePermissions = (permissionsString) => {
    if (!permissionsString) return [];
    try {
      // Handle both stringified array and actual array
      if (typeof permissionsString === 'string') {
        return JSON.parse(permissionsString);
      }
      return permissionsString;
    } catch (error) {
      console.error("Error parsing permissions:", error);
      return [];
    }
  };

  const filteredStaffByTab = staff.filter((staffMember) => {
    if (activeTab === "All") return true;
    if (activeTab === "Access") return staffMember.role_id;
    return true;
  });

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    paginatedData,
    totalItems,
    totalPages,
  } = usePagination(filteredStaffByTab, ["first_name", "last_name", "mobile", "email"]);

  const deleteStaff = async (staffId) => {
    if (!staffId) {
      setError("Staff Id should not be empty");
      return;
    }

    if (window.confirm("Are you sure you want to delete this staff member?")) {
      try {
        setLoading(true);
        setError("");
        const isStaff = window.location.pathname.includes("/restaurant-staff");
        const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
        const res = await api.post(`${apiPrefix}/staff/delete`, { staffId });
        if (res.success || res.data) {
          await fetchStaffData();
        }
      } catch (error) {
        console.error("Error deleting staff:", error);
        setError("Failed to delete staff member");
      } finally {
        setLoading(false);
      }
    }
  };

  const assignRole = async (staffId, roleId) => {
    try {
      setError("");
      const isStaff = window.location.pathname.includes("/restaurant-staff");
      const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
      const res = await api.post(`${apiPrefix}/staff/update`, { 
        staffId,
        roleId: roleId || null 
      });
      if (res.success || res.data) {
        await fetchStaffData();
      }
    } catch (error) {
      console.error("Error assigning role:", error);
      setError("Failed to assign role");
    }
  };

  // wrapper to ensure correct param order when used from StaffCard
  const onAssignRoleWrapper = (staffId, roleId) => {
    return assignRole(staffId, roleId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading staff data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          className="text-2xl text-gray-600 hover:text-gray-800 transition-colors"
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <h1 className="text-xl font-bold text-gray-800">Staff Management</h1>
        <div className="flex items-center gap-3">
          <div className="flex space-x-1">
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setActiveLang(lang)}
                className={`px-2 py-1 text-xs rounded-md font-medium border transition-colors ${
                  activeLang === lang
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300"
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate(`${routePrefix}/roles-management`)}
            className="text-2xl text-primary hover:text-red-700 transition-colors"
            title="Manage Roles"
          >
            <FiUsers />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{staff.length}</p>
          <p className="text-sm text-blue-600">Total Staff</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <p className="text-2xl font-bold text-green-700">{roles.length}</p>
          <p className="text-sm text-green-600">Roles Defined</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 overflow-x-auto mb-4">
        {["All"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm rounded-full transition-colors font-medium ${
              activeTab === tab
                ? "bg-primary text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h3 className="font-semibold text-gray-700">All Staff Members</h3>
        <TableSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search staff by name, phone, or email..." />
      </div>

      {/* Staff List */}
      <div className="space-y-4">
        {paginatedData.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <FiUser className="text-3xl text-gray-400" />
            </div>
            <p className="text-gray-500 mb-2 text-lg">
              {searchTerm ? "No staff members found" : "No staff members"}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate(`${routePrefix}/add-staff`)}
                className="bg-primary text-white px-8 py-3 rounded-full hover:bg-red-700 transition-colors font-medium"
              >
                Add First Staff
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-100">
            <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-4">Staff Member</th>
                  <th className="px-6 py-4">Role</th>
                  <th className="px-6 py-4">Permissions</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedData.map((staffMember) => {
                  const staffIdValue = staffMember.id || staffMember.staffId || staffMember.staff_id || staffMember._id;
                  const staffName = `${staffMember.first_name || ""} ${staffMember.last_name || ""}`.trim() || "Unnamed Staff";
                  const staffRole = getRole(staffMember.role_id);
                  const staffPermissions = parsePermissions(staffMember.permissions);
                  const rolePermissions = staffRole?.permissions || [];
                  const effectivePermissions = staffPermissions.length > 0 ? staffPermissions : rolePermissions;

                  return (
                    <tr 
                      key={staffIdValue} 
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => navigate(`${routePrefix}/staff/edit/${staffIdValue}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <img
                            src={
                              staffMember.profile_image_path ||
                              staffMember.profileImage ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(staffName)}&background=random&color=fff`
                            }
                            alt={staffName}
                            className="w-10 h-10 rounded-full border border-gray-200 object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-gray-900 truncate">
                              {staffName}
                            </div>
                            <div className="text-xs text-gray-500 truncate mt-1">
                              {staffMember.mobile} {staffMember.email && `• ${staffMember.email}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                          staffMember.role_id
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}>
                          {staffMember.role_name || "No Role"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {effectivePermissions.length > 0 ? (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md border border-blue-100">
                            {effectivePermissions.length} rules
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteStaff(staffIdValue);
                            }}
                            title="Delete staff"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {staff.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {/* Floating Add */}
      <button
        onClick={() => navigate(`${routePrefix}/add-staff`)}
        className="fixed bottom-6 right-6 bg-primary text-white p-4 rounded-full shadow-lg hover:bg-red-700 transition-colors hover:shadow-xl"
        title="Add New Staff"
      >
        <FiPlus className="text-2xl" />
      </button>
    </div>
  );
};

const StaffCard = ({
  staffMember,
  roles,
  getRoleName,
  getRole,
  parsePermissions,
  onEdit,
  onPermissions,
  onDelete,
  onAssignRole,
}) => {
  const staffName =
    `${staffMember.first_name || ""} ${staffMember.last_name || ""}`.trim() ||
    "Unnamed Staff";
  const staffRole = getRole(staffMember.role_id);
  
  // Parse permissions from the staff member data
  const staffPermissions = parsePermissions(staffMember.permissions);
  const rolePermissions = staffRole?.permissions || [];
  
  // Use staff-specific permissions if available, otherwise use role permissions
  const effectivePermissions = staffPermissions.length > 0 ? staffPermissions : rolePermissions;

  return (
    <div className="relative bg-white shadow-lg rounded-xl p-4 border border-gray-200 hover:shadow-xl transition-shadow">
      <div className="absolute top-3 right-3 flex gap-2">
        <button
          className="text-blue-600 hover:text-blue-800 p-2"
          onClick={onEdit}
          title="Edit staff"
        >
          <FiEdit2 size={16} />
        </button>
        <button
          className="text-red-600 hover:text-red-800 p-2"
          onClick={onDelete}
          title="Delete staff"
        >
          <FiTrash2 size={16} />
        </button>
      </div>

      <div className="flex items-start gap-4">
        <img
          src={
            staffMember.profile_image_path ||
            staffMember.profileImage ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              staffName
            )}&background=random&color=fff`
          }
          alt={staffName}
          className="w-14 h-14 rounded-full border-2 border-gray-200 object-cover"
        />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-lg text-gray-800 truncate">
            {staffName}
          </p>
          <p className="text-sm text-gray-500">{staffMember.mobile}</p>
          {staffMember.email && (
            <p className="text-sm text-gray-500 truncate">
              {staffMember.email}
            </p>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span
              className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                staffMember.role_id
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-gray-100 text-gray-800 border border-gray-200"
              }`}
            >
              Role: {staffMember.role_name || "No Role"}
            </span>
            {staffMember.restaurant_name && (
              <span className="text-xs px-3 py-1.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-200">
                Branch: {staffMember.restaurant_name}
              </span>
            )}
            {effectivePermissions.length > 0 && (
              <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                {effectivePermissions.length} permissions
              </span>
            )}
           
          </div>

        
        </div>
      </div>

      {/* {staffMember.role_id && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={onPermissions}
            className="border border-primary text-primary text-sm px-4 py-2 rounded-full hover:bg-red-50 transition-colors flex items-center gap-2 font-medium"
          >
            <FiCheckCircle />
            Manage Permissions
          </button>
        </div>
      )} */}
    </div>
  );
};

export default StaffManagement;